import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchPortfolioOrdersDataReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { PortfolioOrdersDataReportApiItem, ReportApiDateRangeQuery } from '../../types/report';
import { gridFormatDecimalString } from './reportGridFormatters';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function portfolioOrdersDataReportRowId(row: PortfolioOrdersDataReportApiItem) {
  return `${row.order_id}-${row.index_fund_id}`;
}

export function PortfolioOrdersDataReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'order_id', headerName: 'Order Id', minWidth: 280, flex: 0.35 },
    { field: 'order_date', headerName: 'Order date', type: 'dateTime', width: 180 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'approved_date', headerName: 'Approved', type: 'dateTime', width: 180 },
    { field: 'index_fund_id', headerName: 'Fund Id', type: 'number', width: 90 },
    { field: 'index_fund_name', headerName: 'Fund name', minWidth: 200, flex: 0.4 },
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 100 },
    { field: 'actual_units', headerName: 'Actual units', width: 130, valueFormatter: gridFormatDecimalString },
    { field: 'actual_amount', headerName: 'Actual amount', width: 130, valueFormatter: gridFormatDecimalString },
    { field: 'expected_units', headerName: 'Expected units', width: 130, valueFormatter: gridFormatDecimalString },
    { field: 'expected_amount', headerName: 'Expected amount', width: 140, valueFormatter: gridFormatDecimalString },
    { field: 'type', headerName: 'Type', width: 130 },
    { field: 'product_name', headerName: 'Product', minWidth: 160, flex: 0.25 },
    { field: 'portfolio_status', headerName: 'Portfolio status', width: 140 },
    { field: 'funding_type', headerName: 'Funding type', minWidth: 150, flex: 0.2 },
  ]);

  const {
    portfolioOrdersDataReportItems,
    totalPortfolioOrdersDataReportItems,
    fetchPortfolioOrdersDataReportStatus,
    error,
  } = useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'portfolio-orders-data-report.csv',
    useCallback(() => reportService.exportPortfolioOrdersDataReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Portfolio orders data</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchPortfolioOrdersDataReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={portfolioOrdersDataReportItems}
          columns={columns.current}
          getRowId={portfolioOrdersDataReportRowId}
          loading={fetchPortfolioOrdersDataReportStatus === fetchStatuses.pending}
          rowCount={totalPortfolioOrdersDataReportItems}
          initialSortModel={[{ field: 'order_date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
